// TASK-947: office messages open in a dialog. The Operations page stays the office card.
import React, { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogContent, DialogTitle, Typography } from "@mui/material";
import getApiBase from "../../../components/getApiBase";
import { adminV2Theme as t } from "../theme";

function formatE164(e164) {
  const digits = String(e164 || "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  return e164 || "";
}

function formatWhen(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function messageLine(message) {
  return [
    formatWhen(message.createdAt),
    message.callerName || "Caller",
    message.callbackNumber || "",
    message.shopName || "",
    message.reason || "",
  ]
    .filter(Boolean)
    .join(" · ");
}

export default function OperationsPage() {
  const [line, setLine] = useState(null);
  const [error, setError] = useState("");
  const [opening, setOpening] = useState(false);
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messagesError, setMessagesError] = useState("");
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${getApiBase()}/api/admin/v2/operations/voice`, {
          credentials: "include",
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(body.error || "Office Voice account could not be read");
        }
        if (!cancelled) setLine(body);
      } catch (err) {
        if (!cancelled) setError(err.message || "Office Voice account could not be read");
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadMessages = async () => {
    setMessagesLoading(true);
    setMessagesError("");
    try {
      const res = await fetch(`${getApiBase()}/api/admin/v2/operations/voice/messages`, {
        credentials: "include",
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || "Office messages could not be read");
      }
      setMessages(body.messages || []);
    } catch (err) {
      setMessages([]);
      setMessagesError(err.message || "Office messages could not be read");
    } finally {
      setMessagesLoading(false);
    }
  };

  const openVoice = async () => {
    setError("");
    setOpening(true);
    try {
      const res = await fetch(`${getApiBase()}/api/admin/v2/operations/voice/open`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: "{}",
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ssoUrl) {
        throw new Error(body.error || "Could not open the office Voice account");
      }
      window.open(body.ssoUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err.message || "Could not open the office Voice account");
    } finally {
      setOpening(false);
    }
  };

  const openMessages = () => {
    setOpenId(null);
    setMessagesOpen(true);
    loadMessages();
  };

  const closeMessages = () => {
    setMessagesOpen(false);
    setOpenId(null);
  };

  const number = formatE164(line?.e164);

  return (
    <Box>
      <Typography sx={{ fontSize: { xs: 20, sm: 22 }, fontWeight: 700, mb: 1 }}>
        Operations
      </Typography>
      <Typography sx={{ color: t.colors.textMuted, fontSize: 14, maxWidth: 560, mb: 2 }}>
        Autoshops.com Office. Greeting, hours, and the assistant stay on the Voice dashboard.
      </Typography>
      <Box
        sx={{
          maxWidth: 420,
          p: 2.5,
          borderRadius: `${t.radius.md}px`,
          border: `1px solid ${t.colors.border}`,
          background: t.colors.surface,
          boxShadow: t.shadow.card,
        }}
      >
        <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
          {line?.name || "Autoshops.com Office"}
        </Typography>
        <Typography sx={{ color: t.colors.textMuted, fontSize: 14, mb: 2 }}>
          {error
            ? "Office number unavailable"
            : number
              ? `${number}${line?.status ? ` · ${line.status}` : ""}`
              : "Loading the office number…"}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            disabled={opening}
            onClick={openVoice}
            sx={{
              background: t.colors.primary,
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { background: t.colors.primaryHover },
            }}
          >
            {opening ? "Opening…" : "Open Voice dashboard"}
          </Button>
          <Button
            variant="outlined"
            onClick={openMessages}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: t.colors.text,
              borderColor: t.colors.borderStrong,
            }}
          >
            Messages
          </Button>
        </Box>
        {error ? (
          <Typography sx={{ color: t.colors.danger, fontSize: 13, mt: 1.5 }}>{error}</Typography>
        ) : null}
      </Box>
      <Dialog open={messagesOpen} onClose={closeMessages} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Messages
          <Button onClick={closeMessages} sx={{ textTransform: "none", color: t.colors.text }}>
            Close
          </Button>
        </DialogTitle>
        <DialogContent>
          {messagesLoading ? (
            <Typography sx={{ color: t.colors.textMuted, fontSize: 14 }}>Loading messages…</Typography>
          ) : null}
          {messagesError ? (
            <Typography sx={{ color: t.colors.danger, fontSize: 14 }}>{messagesError}</Typography>
          ) : null}
          {!messagesLoading && !messagesError && messages.length === 0 ? (
            <Typography sx={{ color: t.colors.textMuted, fontSize: 14 }}>
              No office messages yet.
            </Typography>
          ) : null}
          {messages.map((message) => {
            const open = openId === message.id;
            return (
              <Box key={message.id} sx={{ mb: 1 }}>
                <Button
                  onClick={() => setOpenId(open ? null : message.id)}
                  sx={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    textTransform: "none",
                    color: t.colors.text,
                    fontWeight: 600,
                    fontSize: 14,
                    lineHeight: 1.4,
                    px: 1.5,
                    py: 1,
                    borderRadius: `${t.radius.sm}px`,
                    border: `1px solid ${open ? t.colors.primary : t.colors.border}`,
                    background: open ? t.colors.primarySoft : t.colors.surface,
                  }}
                >
                  {messageLine(message)}
                </Button>
                {open ? (
                  <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
                    <Typography sx={{ color: t.colors.textMuted, fontSize: 13, mb: 0.5 }}>
                      {message.summary || "No summary"}
                    </Typography>
                    <Typography sx={{ color: t.colors.text, fontSize: 13, whiteSpace: "pre-wrap" }}>
                      {message.transcript}
                    </Typography>
                    {message.recordingUrl ? (
                      <Box
                        component="audio"
                        controls
                        src={message.recordingUrl}
                        sx={{ display: "block", width: "100%", mt: 1 }}
                      />
                    ) : null}
                  </Box>
                ) : null}
              </Box>
            );
          })}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
